import {
  CheckCircle, Circle, CloudArrowUp, CircleNotch, Prohibit, ArrowsLeftRight,
  Warning, UploadSimple, Eye,
} from "@phosphor-icons/react";
import { stageLabel, confBadge } from "../../../lib/constants";
import { IntakeInterview } from "../IntakeInterview";
import { GapPanel } from "../GapPanel";
import { ComplianceInterview } from "../ComplianceInterview";
import { DOC_CATEGORIES, HINT_TO_TYPES } from "../portalConstants";

// Documents & Vault tab — a 3-step Docs Stepper on the left and the uploaded-files
// vault on the right. Handles category sub-folder chips, N/A marking, quality-flag
// re-upload, and entity-mismatch move-to-sibling.
export default function DocumentsTab({
  data, entities,
  docCategory, setDocCategory,
  docsStep, setDocsStep,
  consentOk, requireConsent,
  uploadTarget, setUploadTarget, inputRef,
  upload, acceptAnyway, moveDoc, markNa,
  setPreviewDoc,
}) {
  const catMatcher = DOC_CATEGORIES.find((c) => c.v === docCategory)?.match || (() => true);
  const mismatchTarget = (d) => {
    const types = HINT_TO_TYPES[d.ai_entity_hint] || [];
    return entities.find((en) => types.includes(en.entity_type) && en.id !== d.client_id);
  };

  return (
    <div className="space-y-6" data-testid="portal-panel-documents">
      <div className="rounded-lg border border-zinc-200 bg-white p-4 hover:shadow-sm transition-shadow duration-200" data-testid="documents-stepper">
        <div className="grid grid-cols-3 gap-2">
          {[
            { n: 1, label: "Uploads & Checklist", hint: "Upload requested docs" },
            { n: 2, label: "Prior-Year Gap Check", hint: "Fill last year's gaps" },
            { n: 3, label: "IRS Diligence Questions", hint: "Answer compliance Q's" },
          ].map((s) => {
            const active = docsStep === s.n;
            return (
              <button
                key={s.n}
                data-testid={`docs-step-${s.n}`}
                onClick={() => setDocsStep(s.n)}
                className={`text-left rounded-md border p-3 transition-all duration-200 ${active ? "border-slate-900 bg-slate-900 text-white shadow-md" : "border-zinc-200 bg-white hover:border-slate-900 hover:shadow-sm text-slate-800"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${active ? "bg-white text-slate-900" : "bg-slate-900 text-white"}`}>{s.n}</span>
                  <span className="text-xs font-medium">{s.label}</span>
                </div>
                <p className={`text-[10px] ${active ? "text-zinc-300" : "text-zinc-500"}`}>{s.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {docsStep === 1 && (
            <>
              <IntakeInterview checklists={data?.checklists} />

              <div id="portal-checklist" className="rounded-lg border border-zinc-200 bg-white p-5 scroll-mt-16 hover:shadow-md transition-shadow duration-200" data-testid="docs-step-1-panel">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-slate-900">Your document checklist</h3>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-4" data-testid="docs-folders">
                  {DOC_CATEGORIES.map((c) => {
                    const active = docCategory === c.v;
                    const count = (data?.checklists || []).reduce((n, cl) => n + cl.items.filter((i) => c.match(i.doc_type)).length, 0);
                    return (
                      <button
                        key={c.v}
                        data-testid={`docs-folder-${c.v}`}
                        onClick={() => setDocCategory(c.v)}
                        className={`text-[11px] font-medium rounded-md border px-2 py-1 transition-colors duration-150 ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-zinc-200 hover:border-slate-900"}`}>
                        {c.label} <span className={`ml-1 ${active ? "text-zinc-300" : "text-zinc-400"}`}>({count})</span>
                      </button>
                    );
                  })}
                </div>
                {(data?.checklists || []).map((cl) => {
                  const filteredItems = cl.items.filter((i) => catMatcher(i.doc_type));
                  if (filteredItems.length === 0) return null;
                  return (
                    <div key={cl.engagement_id} className="mb-4 last:mb-0" data-testid={`docs-engagement-${cl.engagement_id}`}>
                      <p className="text-xs font-medium text-zinc-600 mb-2">{cl.service_type} · {cl.tax_year} <span className="ml-1 text-[10px] bg-zinc-100 border border-zinc-200 rounded-sm px-1.5 py-0.5">{stageLabel(cl.stage)}</span></p>
                      <div className="space-y-1.5">
                        {filteredItems.map((i) => (
                          <div key={i.doc_type} data-testid={`checklist-item-${i.doc_type}`} className="flex items-center gap-2 text-sm group">
                            {i.received ? <CheckCircle size={16} weight="fill" className="text-emerald-500" /> : <Circle size={16} className="text-zinc-300" />}
                            <span className={`flex-1 ${i.received ? "text-zinc-400 line-through" : "text-zinc-700"}`}>{i.label}</span>
                            {!i.received && (
                              <>
                                <button data-testid={`item-upload-btn-${i.doc_type}`} title={`Upload your ${i.label} directly to this request`}
                                  onClick={() => { if (!consentOk) return requireConsent(); setUploadTarget({ engId: cl.engagement_id, docType: i.doc_type }); inputRef.current?.click(); }}
                                  className="flex items-center gap-0.5 text-[10px] text-slate-600 hover:text-slate-900 border border-transparent hover:border-slate-900 rounded px-1.5 py-0.5 transition-all duration-150">
                                  <UploadSimple size={11} /> Upload
                                </button>
                                <button data-testid={`na-btn-${i.doc_type}`} title="I won't receive this document this year"
                                  onClick={() => markNa.mutate({ engId: cl.engagement_id, docType: i.doc_type })}
                                  className="opacity-100 md:opacity-0 md:group-hover:opacity-100 flex items-center gap-0.5 text-[10px] text-zinc-400 hover:text-rose-600 transition-colors duration-150">
                                  <Prohibit size={11} /> N/A
                                </button>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <button data-testid={`portal-upload-btn-${cl.engagement_id}`} onClick={() => { if (!consentOk) return requireConsent(); setUploadTarget({ engId: cl.engagement_id, docType: null }); inputRef.current?.click(); }}
                        className="mt-3 flex items-center gap-1.5 text-xs text-slate-700 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 hover:border-slate-900 hover:shadow-sm transition-all duration-150">
                        {upload.isPending && uploadTarget?.engId === cl.engagement_id ? <CircleNotch size={13} className="animate-spin" /> : <CloudArrowUp size={13} />} Upload documents (batch OK)
                      </button>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end">
                <button data-testid="docs-step-next-2" onClick={() => setDocsStep(2)} className="text-xs text-slate-800 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 hover:border-slate-900 transition-colors duration-150">
                  Next: Prior-year gap check →
                </button>
              </div>
            </>
          )}
          {docsStep === 2 && (
            <div data-testid="docs-step-2-panel">
              <GapPanel />
              <div className="flex justify-between mt-4">
                <button data-testid="docs-step-back-1" onClick={() => setDocsStep(1)} className="text-xs text-zinc-600 hover:text-slate-900 transition-colors duration-150">← Uploads</button>
                <button data-testid="docs-step-next-3" onClick={() => setDocsStep(3)} className="text-xs text-slate-800 border border-zinc-200 rounded-md px-3 py-1.5 hover:bg-zinc-50 hover:border-slate-900 transition-colors duration-150">
                  Next: IRS diligence →
                </button>
              </div>
            </div>
          )}
          {docsStep === 3 && (
            <div data-testid="docs-step-3-panel">
              <ComplianceInterview checklists={data?.checklists} />
              <div className="flex justify-start mt-4">
                <button data-testid="docs-step-back-2" onClick={() => setDocsStep(2)} className="text-xs text-zinc-600 hover:text-slate-900 transition-colors duration-150">← Gap check</button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div className="rounded-lg border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow duration-200">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Your uploads &amp; shared vault</h3>
            {(data?.documents || []).map((d) => {
              const target = d.entity_mismatch ? mismatchTarget(d) : null;
              return (
                <div key={d.id} className="py-1.5 border-b border-zinc-100 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-mono text-xs text-zinc-700 truncate block max-w-[220px]">{d.filename}</span>
                      {d.uploaded_by?.endsWith("@firm.com") && <span className="text-[9px] text-zinc-400">Shared by your firm</span>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {d.status === "processing"
                        ? <span className="text-[10px] text-zinc-400 flex items-center gap-1"><CircleNotch size={11} className="animate-spin" /> processing</span>
                        : d.status === "classification_failed"
                          ? <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 rounded-sm px-1.5 py-0.5">Retake photo</span>
                          : d.status === "needs_review"
                            ? <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 rounded-sm px-1.5 py-0.5 flex items-center gap-1"><Warning size={10} /> Quality check</span>
                            : <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${confBadge(d.confidence)}`}>{d.doc_type ? `${d.doc_type} ✓ filed` : "—"}</span>}
                      <button data-testid={`preview-btn-${d.id}`} title="Preview" onClick={() => setPreviewDoc(d)} className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150"><Eye size={14} /></button>
                    </div>
                  </div>
                  {d.status === "needs_review" && (
                    <div className="mt-1.5 rounded-md border border-amber-200 bg-amber-50/60 p-2" data-testid={`quality-flag-${d.id}`}>
                      <p className="text-[10px] text-amber-800">{(d.quality_issues || []).join(" · ") || "We couldn't read this file clearly."}</p>
                      <div className="flex gap-3 mt-1">
                        <button data-testid={`reupload-btn-${d.id}`}
                          onClick={() => { setUploadTarget({ engId: d.engagement_id, docType: d.checklist_doc_type }); inputRef.current?.click(); }}
                          className="text-[10px] text-amber-900 underline hover:no-underline">Re-upload a clearer copy</button>
                        <button data-testid={`accept-anyway-btn-${d.id}`} onClick={() => acceptAnyway.mutate(d.id)}
                          className="text-[10px] text-zinc-600 underline hover:no-underline">Use anyway (manual staff review)</button>
                      </div>
                    </div>
                  )}
                  {d.entity_mismatch && target && (
                    <div className="mt-1.5 rounded-md border border-indigo-200 bg-indigo-50/60 p-2" data-testid={`mismatch-flag-${d.id}`}>
                      <p className="text-[10px] text-indigo-800">AI cross-check: this looks like it belongs to <span className="font-medium">{target.name}</span>, not this entity.</p>
                      <button data-testid={`move-doc-btn-${d.id}`} disabled={moveDoc.isPending} onClick={() => moveDoc.mutate({ id: d.id, target: target.id })}
                        className="mt-1 flex items-center gap-1 text-[10px] text-indigo-900 underline hover:no-underline">
                        <ArrowsLeftRight size={11} /> Move it to {target.name}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {!data?.documents?.length && <p className="text-xs text-zinc-400 italic">Nothing uploaded yet — start with your checklist on the left.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
